// Types for Journeys V2 feature

export interface ClusterSummary {
  id: number;
  label: string;
  key: string;
}

export interface DateRange {
  start: string;
  end: string;
}

export interface TopicSummary {
  cluster: ClusterSummary;
  conversation_count: number;
  total_count?: number;
  example_values: string[];
}

export interface BackAndForthPattern {
  cluster_a: ClusterSummary;
  cluster_b: ClusterSummary;
  conversation_count: number;
  example_snippets: ConversationSnippet[];
}

export interface ConversationSnippet {
  session_id: string;
  steps: ConversationStep[];
}

export interface ConversationStep {
  cluster_label: string;
  value: string;
}

export interface OverviewStats {
  total_conversations: number;
  total_transitions: number;
  date_range: DateRange;
}

export interface DropoffPoint {
  cluster: ClusterSummary;
  dropoff_count: number;
  dropoff_rate: number;
  total_visits: number;
  example_values: string[];
}

export interface OverviewData {
  starting_topics: TopicSummary[];
  common_followups: TopicSummary[];
  back_and_forth_patterns: BackAndForthPattern[];
  dropoff_points: DropoffPoint[];
  stats: OverviewStats;
}

export interface NextStep {
  cluster: ClusterSummary;
  percentage: number;
  conversation_count: number;
  example_value: string | null;
}

export interface NextStepsData {
  current_topic: ClusterSummary;
  next_steps: NextStep[];
  return_to_topic_count: number;
  total_conversations_with_topic: number;
  ended_conversation_count: number;
  continuation_percentage: number;
}

// Path-based next steps data
// Shows what users do AFTER following a specific path (e.g., A → B → C → ?)
export interface PathNextStepsData {
  path: ClusterSummary[];
  path_followers_count: number;
  next_steps: NextStep[];
  return_to_path_end_count: number;
  ended_conversation_count: number;
  continuation_percentage: number;
}

export interface ExampleValue {
  value: string;
  count: number;
}

export interface TopicExamplesData {
  cluster: ClusterSummary;
  example_values: ExampleValue[];
  example_sequences: ConversationSnippet[];
}

export interface StepExample {
  from_value: string;
  to_value: string;
  user_id: string;
  session_id: string | null;
}

export interface StepExamplesData {
  from_topic: ClusterSummary;
  to_topic: ClusterSummary;
  examples: StepExample[];
}

// Drop-off deep dive types
export interface DropoffStats {
  total_visits: number;
  continued_count: number;
  dropoff_count: number;
  dropoff_rate: number;
}

export interface IncomingPath {
  previous_topic: ClusterSummary;
  session_count: number;
  percentage: number;
}

export interface DropoffDeepDiveData {
  cluster: ClusterSummary;
  dropoff_stats: DropoffStats;
  dropoff_examples: string[];
  continued_examples: string[];
  incoming_paths: IncomingPath[];
}

// Time range options for the filter
export type TimeRangeOption = 7 | 14 | 30 | 90;

export interface TimeRangeChoice {
  value: TimeRangeOption;
  label: string;
}

export const TIME_RANGE_OPTIONS: TimeRangeChoice[] = [
  { value: 7, label: 'Last 7 days' },
  { value: 14, label: 'Last 14 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];
